/**
 * Cabeceras de la pagina: titulo, descripcion, canonical, Open Graph y Twitter.
 *
 * Se monta una sola vez por vista, siempre lo mas arriba posible del arbol de
 * esa vista. Es el unico lugar del sitio que escribe en el `<head>`: si dos
 * componentes pelearan por el titulo, ganaria el ultimo en montarse y nadie
 * sabria por que.
 *
 * `titulo` llega sin la marca; la marca se agrega aca para que ninguna pantalla
 * tenga que acordarse.
 */
import { Helmet } from 'react-helmet-async';

import { SITIO } from '../configuracion/sitio';
import { urlAbsoluta } from './urls';

/** Largo con el que Google todavia muestra el titulo completo. */
const LARGO_TITULO = 60;
const LARGO_DESCRIPCION = 155;

export interface SeoProps {
  /** Titulo de la pagina, sin el nombre de la tienda. */
  titulo: string;
  descripcion: string;
  /** Ruta interna de esta vista; se publica como canonical absoluta. */
  ruta: string;
  /** Imagen para compartir en redes y WhatsApp. Ruta interna o URL absoluta. */
  imagen?: string | undefined;
  /** `product` en la ficha; `website` en el resto. */
  tipo?: 'website' | 'product';
  /** Pantallas que no deben indexarse: backoffice y resultados de busqueda. */
  sinIndexar?: boolean;
}

function recortar(texto: string, largo: number): string {
  return texto.length <= largo ? texto : `${texto.slice(0, largo - 1).trimEnd()}…`;
}

/**
 * Agrega la marca al final del titulo, salvo que ya venga incluida.
 *
 * Importa porque el titulo puede venir de dos lados: del nombre del producto,
 * que nunca trae la marca, o del titulo SEO que escribe el administrador
 * (ADM-08), que casi siempre la escribe. Sin esta comprobacion, lo segundo
 * termina en "Producto | El Carpintero | El Carpintero".
 */
function conMarca(titulo: string): string {
  return titulo.toLowerCase().includes(SITIO.nombre.toLowerCase())
    ? titulo
    : `${titulo} | ${SITIO.nombre}`;
}

export function Seo({
  titulo,
  descripcion,
  ruta,
  imagen,
  tipo = 'website',
  sinIndexar = false,
}: SeoProps) {
  const tituloCompleto = recortar(conMarca(titulo), LARGO_TITULO);
  const resumen = recortar(descripcion, LARGO_DESCRIPCION);
  const canonical = urlAbsoluta(ruta);
  const imagenAbsoluta = imagen !== undefined ? urlAbsoluta(imagen) : undefined;

  return (
    <Helmet prioritizeSeoTags>
      <title>{tituloCompleto}</title>
      <meta name="description" content={resumen} />
      <link rel="canonical" href={canonical} />
      {sinIndexar ? <meta name="robots" content="noindex, follow" /> : null}

      <meta property="og:type" content={tipo} />
      <meta property="og:site_name" content={SITIO.nombreLargo} />
      <meta property="og:locale" content="es_CL" />
      <meta property="og:title" content={tituloCompleto} />
      <meta property="og:description" content={resumen} />
      <meta property="og:url" content={canonical} />
      {imagenAbsoluta !== undefined ? <meta property="og:image" content={imagenAbsoluta} /> : null}

      <meta name="twitter:card" content={imagenAbsoluta !== undefined ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={tituloCompleto} />
      <meta name="twitter:description" content={resumen} />
      {imagenAbsoluta !== undefined ? <meta name="twitter:image" content={imagenAbsoluta} /> : null}
    </Helmet>
  );
}
